import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AlertCircle, X, Phone, ChevronDown, UserPlus, Plus, Search } from 'lucide-react';
import {
  api,
  getStoresByEnterprise,
  getAssistants,
  createAssistant,
  updateAssistant,
  deleteAssistant,
  toggleAssistantStatus,
} from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

// Matches the backend's US phone validation (GlobalUtils.isValidUsPhoneNumber):
// an optional leading +1, then a standard 10-digit US number.
const US_PHONE_REGEX = /^(\+1[-.\s]?)?(\(?[2-9]\d{2}\)?[-.\s]?)?[2-9]\d{2}[-.\s]?\d{4}$/;

const isValidUsPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || !phoneNumber.trim()) return false;
  let digits = phoneNumber.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) return false;
  return US_PHONE_REGEX.test(phoneNumber);
};

const PHONE_ERROR = 'Enter a valid US phone number, e.g. +14074900265.';

// Computes field-level errors for the assignment chain + phone. The chain is evaluated
// top to bottom (enterprise -> BOC -> store) so only the first unmet step is ever flagged.
const computeErrors = ({ enterpriseId, bocId, storeId, phoneNumber }) => {
  const errors = {};
  if (!enterpriseId) {
    errors.enterprise = 'Select an enterprise.';
  } else if (!bocId) {
    errors.boc = 'Select a BOC.';
  } else if (!storeId) {
    errors.store = 'Select a store.';
  }
  if (!isValidUsPhoneNumber(phoneNumber)) {
    errors.phone = PHONE_ERROR;
  }
  return errors;
};

const TEXT_INPUT_CLASSES =
  'block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm transition-all duration-200';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// +14074900265 -> (407) 490-0265. Anything that isn't a 10-digit US number is shown as stored.
const formatPhone = (phoneNumber) => {
  if (!phoneNumber) return '';
  let digits = phoneNumber.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return phoneNumber;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const getInitials = (assistant) =>
  `${(assistant.firstName || '').trim().charAt(0)}${(assistant.lastName || '').trim().charAt(0)}`.toUpperCase();

const FIELD_ORDER = ['enterprise', 'boc', 'store', 'phone'];
const EMPTY_TOUCHED = { enterprise: false, boc: false, store: false, phone: false };

const fieldInputClasses = (hasError) =>
  `block w-full pl-3 pr-10 py-2.5 text-base rounded-lg shadow-sm transition-all duration-200 bg-white appearance-none focus:outline-none focus:ring-2 sm:text-sm ${
    hasError
      ? 'border border-red-400 focus:ring-red-500 focus:border-red-500'
      : 'border border-gray-300 focus:ring-purple-500 focus:border-purple-500'
  }`;

const AssistantManagement = () => {
  const [assistants, setAssistants] = useState([]);
  const [stores, setStores] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();

  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedBoc, setSelectedBoc] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loadingBOCs, setLoadingBOCs] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);

  // Which required fields the user has interacted with, and their current errors.
  // Errors only render once a field is touched (or a submit attempt touches everything) -
  // never on every keystroke.
  const [touched, setTouched] = useState(EMPTY_TOUCHED);
  const [fieldErrors, setFieldErrors] = useState({});

  // List view: all stores (to show store names instead of raw FK ids), search and status filter.
  const [allStores, setAllStores] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const storeById = useMemo(
    () => Object.fromEntries(allStores.map((store) => [store.id, store])),
    [allStores]
  );

  const filteredAssistants = useMemo(() => {
    const term = search.trim().toLowerCase();
    const termDigits = term.replace(/\D/g, '');
    return assistants.filter((assistant) => {
      if (statusFilter === 'active' && !assistant.active) return false;
      if (statusFilter === 'inactive' && assistant.active) return false;
      if (!term) return true;
      const store = storeById[assistant.storeIdFk];
      const haystack = [
        assistant.firstName,
        assistant.lastName,
        assistant.phoneNumber,
        store && store.name,
        store && store.storeId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (haystack.includes(term)) return true;
      return termDigits.length >= 3 && (assistant.phoneNumber || '').replace(/\D/g, '').includes(termDigits);
    });
  }, [assistants, search, statusFilter, storeById]);

  const enterpriseRef = useRef(null);
  const bocRef = useRef(null);
  const storeRef = useRef(null);
  const phoneRef = useRef(null);
  const fieldRefs = { enterprise: enterpriseRef, boc: bocRef, store: storeRef, phone: phoneRef };

  const [formData, setFormData] = useState({
    phoneNumber: '',
    storeIdFk: '',
    enterpriseIdFk: '',
    firstName: '',
    lastName: '',
    active: false,
  });

  useEffect(() => {
    loadAssistants();
    loadEnterprises();
    loadAllStores();
  }, []);

  // Drawer behaviour: Esc closes it, and the page behind it doesn't scroll while it's open.
  useEffect(() => {
    if (!showCreateForm) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') cancelEdit();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showCreateForm]);

  const loadAllStores = async () => {
    try {
      const response = await api.get('/admin/stores');
      setAllStores(response.data || []);
    } catch (error) {
      // Non-fatal: the list falls back to showing the store's internal id.
      console.error('Error loading stores:', error);
    }
  };

  const loadAssistants = async () => {
    setError('');
    try {
      const data = await getAssistants();
      setAssistants(data);
    } catch (error) {
      console.error('Error loading assistants:', error);
      setError('Failed to load assistants');
    } finally {
      setLoading(false);
    }
  };

  const loadEnterprises = async () => {
    setError('');
    try {
      const response = await api.get('/admin/enterprises');
      setEnterprises(response.data);
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setError('Failed to load enterprises');
    }
  };

  const loadBocsByEnterprise = async (enterpriseId) => {
    setLoadingBOCs(true);
    setError('');
    try {
      const response = await api.get(`/admin/bocs/enterprise/${enterpriseId}`);
      setBocs(response.data);
    } catch (error) {
      console.error('Error loading BOCs:', error);
      setError('Failed to load BOCs');
    } finally {
      setLoadingBOCs(false);
    }
  };

  const loadStoresByBoc = async (bocId) => {
    setLoadingStores(true);
    setError('');
    try {
      const response = await api.get(`/admin/stores/boc/${bocId}`);
      setStores(response.data);
    } catch (error) {
      console.error('Error loading stores:', error);
      setError('Failed to load stores');
    } finally {
      setLoadingStores(false);
    }
  };

  const handleEnterpriseChange = (enterpriseId) => {
    setSelectedEnterprise(enterpriseId);
    setSelectedBoc(null);
    setSelectedStore(null);
    setBocs([]);
    setStores([]);
    setFormData({ ...formData, enterpriseIdFk: enterpriseId, storeIdFk: '' });
    setTouched((t) => ({ ...t, enterprise: true, boc: false, store: false }));
    setFieldErrors(
      computeErrors({
        enterpriseId,
        bocId: null,
        storeId: null,
        phoneNumber: formData.phoneNumber,
      })
    );
    if (enterpriseId) {
      loadBocsByEnterprise(enterpriseId);
    }
  };

  const handleBocChange = (bocId) => {
    setSelectedBoc(bocId);
    setSelectedStore(null);
    setStores([]);
    setFormData({ ...formData, storeIdFk: '' });
    setTouched((t) => ({ ...t, boc: true, store: false }));
    setFieldErrors(
      computeErrors({
        enterpriseId: selectedEnterprise,
        bocId,
        storeId: null,
        phoneNumber: formData.phoneNumber,
      })
    );
    if (bocId) {
      loadStoresByBoc(bocId);
    }
  };

  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
    setFormData({ ...formData, storeIdFk: storeId });
    setTouched((t) => ({ ...t, store: true }));
    setFieldErrors(
      computeErrors({
        enterpriseId: selectedEnterprise,
        bocId: selectedBoc,
        storeId,
        phoneNumber: formData.phoneNumber,
      })
    );
  };

  const handlePhoneBlur = () => {
    setTouched((t) => ({ ...t, phone: true }));
    setFieldErrors((prev) => ({
      ...prev,
      phone: isValidUsPhoneNumber(formData.phoneNumber) ? undefined : PHONE_ERROR,
    }));
  };

  // Validates the whole form on submit. Returns true when valid. When invalid, it marks
  // every field touched (so all relevant errors render) and scrolls/focuses the first one,
  // in field order, rather than leaving the user to hunt for what's wrong.
  const validateAndFocus = () => {
    const errors = computeErrors({
      enterpriseId: selectedEnterprise,
      bocId: selectedBoc,
      storeId: selectedStore,
      phoneNumber: formData.phoneNumber,
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      return true;
    }
    setTouched({ enterprise: true, boc: true, store: true, phone: true });
    const firstInvalidField = FIELD_ORDER.find((field) => errors[field]);
    const ref = fieldRefs[firstInvalidField];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      ref.current.focus();
    }
    return false;
  };

  const handleCreateAssistant = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (!validateAndFocus()) return;
    setSubmitting(true);
    try {
      await createAssistant(formData);
      await loadAssistants();
      setShowCreateForm(false);
      resetForm();
      showSuccess('Assistant created');
    } catch (error) {
      console.error('Error creating assistant:', error);
      setError(error.message || 'Failed to create assistant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAssistant = async (e) => {
    e.preventDefault();
    if (!editingAssistant || submitting) return;

    setError('');
    if (!validateAndFocus()) return;
    setSubmitting(true);
    try {
      await updateAssistant(editingAssistant.id, formData);
      await loadAssistants();
      setEditingAssistant(null);
      resetForm();
      showSuccess('Assistant updated');
    } catch (error) {
      console.error('Error updating assistant:', error);
      setError(error.message || 'Failed to update assistant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAssistant = async (id) => {
    const assistant = assistants.find((a) => a.id === id);
    const label = assistant
      ? `${assistant.firstName || ''} ${assistant.lastName || ''}`.trim() || assistant.phoneNumber
      : 'this assistant';
    const confirmed = await confirm({
      title: `Delete assistant ${label}?`,
      message: assistant
        ? `The assistant on ${assistant.phoneNumber} will be permanently deleted and inbound messages to that number will no longer be routed.`
        : 'This assistant will be permanently deleted.',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setError('');
    try {
      await deleteAssistant(id);
      await loadAssistants();
      showSuccess(`Assistant ${label} deleted`);
    } catch (error) {
      console.error('Error deleting assistant:', error);
      setError(error.message || 'Failed to delete assistant');
    }
  };

  const handleToggleStatus = async (id) => {
    const assistant = assistants.find((a) => a.id === id);
    setError('');
    try {
      await toggleAssistantStatus(id);
      await loadAssistants();
      showSuccess(assistant ? `Assistant ${assistant.active ? 'disabled' : 'enabled'}` : 'Assistant status updated');
    } catch (error) {
      console.error('Error toggling status:', error);
      setError(error.message || 'Failed to toggle status');
    }
  };

  const resetForm = () => {
    setFormData({
      phoneNumber: '',
      storeIdFk: '',
      enterpriseIdFk: '',
      firstName: '',
      lastName: '',
      active: false,
    });
    setSelectedEnterprise(null);
    setSelectedBoc(null);
    setSelectedStore(null);
    setBocs([]);
    setStores([]);
    setTouched(EMPTY_TOUCHED);
    setFieldErrors({});
  };

  const startEdit = async (assistant) => {
    setEditingAssistant(assistant);
    setError('');
    setTouched(EMPTY_TOUCHED);
    setFieldErrors({});
    setFormData({
      phoneNumber: assistant.phoneNumber,
      storeIdFk: assistant.storeIdFk,
      enterpriseIdFk: assistant.enterpriseIdFk || '',
      firstName: assistant.firstName || '',
      lastName: assistant.lastName || '',
      active: assistant.active,
    });
    setSelectedEnterprise(assistant.enterpriseIdFk || null);
    setSelectedBoc(null);
    setSelectedStore(assistant.storeIdFk || null);
    setBocs([]);
    setStores([]);
    setShowCreateForm(true);

    if (!assistant.enterpriseIdFk) {
      return;
    }

    await loadBocsByEnterprise(assistant.enterpriseIdFk);
    if (assistant.storeIdFk) {
      try {
        const enterpriseStores = await getStoresByEnterprise(assistant.enterpriseIdFk);
        const matchingStore = enterpriseStores.find((s) => s.id === assistant.storeIdFk);
        if (matchingStore && matchingStore.bocIdFk) {
          setSelectedBoc(matchingStore.bocIdFk);
          await loadStoresByBoc(matchingStore.bocIdFk);
          setSelectedStore(assistant.storeIdFk);
        }
      } catch (error) {
        console.error('Error resolving BOC for assistant store:', error);
      }
    }
  };

  const cancelEdit = () => {
    setEditingAssistant(null);
    setShowCreateForm(false);
    setError('');
    resetForm();
  };

  if (loading) return <AdminLoading message="Loading assistants..." />;

  const openCreate = () => {
    setShowCreateForm(true);
    setEditingAssistant(null);
    setError('');
    resetForm();
  };

  const isEditing = Boolean(editingAssistant);
  const hasFilters = search.trim() !== '' || statusFilter !== 'all';

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      <AdminPageHeader
        icon={UserPlus}
        title="Assistant Management"
        subtitle="People who receive and answer inbound leads for a store"
      />

      {error && !showCreateForm && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Assistants list */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">Assistants</h3>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {hasFilters ? `${filteredAssistants.length} of ${assistants.length}` : assistants.length}
            </span>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, phone or store"
                className="w-full sm:w-72 pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <div className="inline-flex rounded-lg border border-gray-300 p-0.5 bg-gray-50" role="group" aria-label="Filter by status">
              {STATUS_FILTERS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatusFilter(option.value)}
                  aria-pressed={statusFilter === option.value}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    statusFilter === option.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg text-white bg-purple-600 hover:bg-purple-700 transition-colors shadow-sm"
            >
              <Plus className="h-4 w-4" />
              Add Assistant
            </button>
          </div>
        </div>

        {assistants.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-purple-50">
              <UserPlus className="h-6 w-6 text-purple-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">No assistants yet</p>
            <p className="mt-1 text-sm text-gray-500">Add an assistant to start routing a store's inbound leads to them.</p>
            <button
              type="button"
              onClick={openCreate}
              className="mt-5 inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Assistant
            </button>
          </div>
        ) : filteredAssistants.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-600">No assistants match your filters.</p>
            <button
              type="button"
              onClick={() => { setSearch(''); setStatusFilter('all'); }}
              className="mt-2 text-sm font-medium text-purple-600 hover:text-purple-800"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Assistant</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Store</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Enterprise</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredAssistants.map((assistant) => {
                  const fullName = `${assistant.firstName || ''} ${assistant.lastName || ''}`.trim();
                  const store = storeById[assistant.storeIdFk];
                  return (
                    <tr
                      key={assistant.id}
                      onDoubleClick={() => startEdit(assistant)}
                      title="Double-click to edit"
                      className="hover:bg-gray-50 transition-colors cursor-pointer select-none"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                              assistant.active ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {getInitials(assistant) || <Phone className="h-4 w-4" />}
                          </div>
                          <div className="min-w-0">
                            <div className={`text-sm font-medium ${fullName ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                              {fullName || 'Unnamed assistant'}
                            </div>
                            <div className="text-sm text-gray-500 tabular-nums">{formatPhone(assistant.phoneNumber)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {store ? (
                          <>
                            <div className="text-sm text-gray-900">{store.name || store.storeId}</div>
                            {store.name && (
                              <div className="text-xs text-gray-400 truncate max-w-[220px]" title={store.storeId}>
                                {store.storeId}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">#{assistant.storeIdFk}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {enterprises.find((ent) => ent.id === assistant.enterpriseIdFk)?.name || assistant.enterpriseIdFk || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            assistant.active ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' : 'bg-gray-100 text-gray-600 ring-1 ring-gray-500/20'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${assistant.active ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                          {assistant.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm font-medium"
                        onDoubleClick={(e) => e.stopPropagation()}
                      >
                        <AdminRowActions align="end">
                          <AdminActionButton variant="edit" onClick={() => startEdit(assistant)} />
                          <AdminActionButton variant={assistant.active ? 'deactivate' : 'activate'} onClick={() => handleToggleStatus(assistant.id)} />
                          <AdminActionButton variant="delete" onClick={() => handleDeleteAssistant(assistant.id)} />
                        </AdminRowActions>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit drawer - slides over the list instead of pushing it down the page */}
      {showCreateForm && (
        <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="assistant-drawer-title">
          <div className="absolute inset-0 bg-gray-900/40" onClick={cancelEdit} aria-hidden="true" />

          <form
            onSubmit={isEditing ? handleUpdateAssistant : handleCreateAssistant}
            noValidate
            className="relative flex h-full w-full max-w-[480px] flex-col bg-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
              <div>
                <h3 id="assistant-drawer-title" className="text-lg font-semibold text-gray-900">
                  {isEditing ? 'Edit assistant' : 'New assistant'}
                </h3>
                <p className="mt-0.5 text-sm text-gray-500">
                  {isEditing
                    ? `${`${editingAssistant.firstName || ''} ${editingAssistant.lastName || ''}`.trim() || 'Unnamed'} · ${formatPhone(editingAssistant.phoneNumber)}`
                    : 'Pick the store they work for, then their contact details.'}
                </p>
              </div>
              <button
                type="button"
                onClick={cancelEdit}
                aria-label="Close"
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <section>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Assignment</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Enterprise <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        ref={enterpriseRef}
                        value={selectedEnterprise || ''}
                        onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : null)}
                        className={fieldInputClasses(touched.enterprise && fieldErrors.enterprise)}
                      >
                        <option value="">Select enterprise</option>
                        {enterprises.map((ent) => (
                          <option key={ent.id} value={ent.id}>{ent.name}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                        <ChevronDown className="h-4 w-4" />
                      </div>
                    </div>
                    {touched.enterprise && fieldErrors.enterprise && (
                      <p className="mt-1.5 text-xs text-red-600">{fieldErrors.enterprise}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      BOC <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        ref={bocRef}
                        value={selectedBoc || ''}
                        onChange={(e) => handleBocChange(e.target.value ? Number(e.target.value) : null)}
                        disabled={!selectedEnterprise}
                        className={`${fieldInputClasses(touched.boc && fieldErrors.boc)} disabled:bg-gray-50 disabled:text-gray-400 disabled:border-dashed disabled:cursor-not-allowed`}
                      >
                        <option value="">{selectedEnterprise ? 'Select BOC' : 'Choose an enterprise first'}</option>
                        {loadingBOCs ? (
                          <option disabled>Loading...</option>
                        ) : (
                          bocs.map((boc) => (
                            <option key={boc.id} value={boc.id}>
                              {boc.name ? `${boc.name} (${boc.bocId})` : boc.bocId}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                        {loadingBOCs ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </div>
                    {touched.boc && fieldErrors.boc && (
                      <p className="mt-1.5 text-xs text-red-600">{fieldErrors.boc}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Store <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        ref={storeRef}
                        value={selectedStore || ''}
                        onChange={(e) => handleStoreChange(e.target.value ? Number(e.target.value) : null)}
                        disabled={!selectedBoc}
                        className={`${fieldInputClasses(touched.store && fieldErrors.store)} disabled:bg-gray-50 disabled:text-gray-400 disabled:border-dashed disabled:cursor-not-allowed`}
                      >
                        <option value="">{selectedBoc ? 'Select store' : 'Choose a BOC first'}</option>
                        {loadingStores ? (
                          <option disabled>Loading...</option>
                        ) : (
                          stores.map((store) => (
                            <option key={store.id} value={store.id}>
                              {store.name ? `${store.name} (${store.storeId})` : store.storeId}
                            </option>
                          ))
                        )}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                        {loadingStores ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </div>
                    {touched.store && fieldErrors.store && (
                      <p className="mt-1.5 text-xs text-red-600">{fieldErrors.store}</p>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Contact</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">First name</label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className={TEXT_INPUT_CLASSES}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Last name</label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className={TEXT_INPUT_CLASSES}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Mobile number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        ref={phoneRef}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={formData.phoneNumber}
                        onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        onBlur={handlePhoneBlur}
                        className={`${fieldInputClasses(touched.phone && fieldErrors.phone).replace('pl-3 pr-10', 'pl-9 pr-3')} tabular-nums`}
                        placeholder="+1 407 490 0265"
                      />
                    </div>
                    {touched.phone && fieldErrors.phone ? (
                      <p className="mt-1.5 text-xs text-red-600">{fieldErrors.phone}</p>
                    ) : (
                      <p className="mt-1.5 text-xs text-gray-500">US number. Leads for this store are texted here.</p>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <label
                  htmlFor="assistant-active"
                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                    formData.active ? 'border-purple-300 bg-purple-50/50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    id="assistant-active"
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm">
                    <span className="block font-medium text-gray-900">{isEditing ? 'Active' : 'Activate now'}</span>
                    <span className="block text-gray-500 mt-0.5">
                      {isEditing
                        ? 'While on, this assistant receives and responds to leads.'
                        : 'Leave off to review the assistant before it starts receiving leads.'}
                    </span>
                  </span>
                </label>
              </section>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEditing ? 'Saving...' : 'Creating...'}
                  </>
                ) : isEditing ? (
                  'Save changes'
                ) : (
                  'Create assistant'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AssistantManagement;
